"use client";

import { router } from "@inertiajs/react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrencyWithSymbol } from "@/helpers/currency-formatter";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { TRUNCATE_THRESHOLD } from "@/lib/constants";
import type { AllQuoteItem } from "@/types";

interface QuoteItemSelectorProps {
  requestItemId: number;
  allSupplierQuoteItems: AllQuoteItem[];
  currentQuoteItemId?: number | null;
  onAssign?: () => void;
}

// Use robust currency.js-based formatter

export default function QuoteItemSelector({
  requestItemId,
  allSupplierQuoteItems,
  currentQuoteItemId,
  onAssign,
}: QuoteItemSelectorProps) {
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const routes = useOrganizationRoutes();

  const options = [
    {
      id: null,
      label: "Sin asignar",
      description: "Quitar asignación",
      isAssigned: false,
      assignedTo: null,
    },
    ...allSupplierQuoteItems.map((item) => ({
      id: item.id,
      label: item.supplier_product_name,
      description: `${formatCurrencyWithSymbol(item.unit_price, item.currency)} - ${item.quantity} ${item.unit}`,
      isAssigned: item.is_assigned && item.assigned_to_id !== requestItemId,
      assignedTo: item.assigned_to_description,
    })),
  ];

  // Find current selection
  const currentSelection = options.find(
    (option) => option.id === currentQuoteItemId,
  );

  const handleSelect = async (selectedId: number | null) => {
    setProcessing(true);

    try {
      // Step 1: If there's a currently assigned quote item, unassign it first
      if (currentQuoteItemId && currentQuoteItemId !== selectedId) {
        await new Promise((resolve, reject) => {
          router.patch(
            routes.quoteItems.show(currentQuoteItemId),
            {
              quote_item: {
                request_item_id: null,
              },
            },
            {
              preserveScroll: true,
              onSuccess: resolve,
              onError: reject,
            },
          );
        });
      }

      // Step 2: If a new quote item is selected, assign it
      if (selectedId) {
        await new Promise((resolve, reject) => {
          router.patch(
            routes.quoteItems.show(selectedId),
            {
              quote_item: {
                request_item_id: requestItemId,
              },
            },
            {
              preserveScroll: true,
              onSuccess: resolve,
              onError: reject,
            },
          );
        });
      }

      // Success: close modal and refresh
      setOpen(false);
      onAssign?.();
    } catch (errors) {
      console.error("Error updating quote item assignment:", errors);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between text-left h-auto min-h-8 px-3 py-2"
          disabled={processing}
        >
          {currentSelection?.id ? (
            <div className="flex flex-col items-start min-w-0 space-y-1">
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  ✓ {currentSelection.label}
                </span>
              </div>
              <span className="text-xs text-muted-foreground truncate max-w-full">
                {currentSelection.description}
              </span>
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">
              Seleccionar producto...
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar producto..." className="h-9" />
          <CommandList>
            <CommandEmpty>No se encontraron productos.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isTruncated = option.label.length > TRUNCATE_THRESHOLD;

                const itemContent = (
                  <div className="flex items-start w-full px-2 py-3">
                    <div className="flex flex-col items-start min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {option.label}
                        </span>
                        {option.isAssigned && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700 shrink-0">
                            Asignado
                          </span>
                        )}
                      </div>
                      {option.description && (
                        <span className="text-xs text-muted-foreground mt-1">
                          {option.description}
                        </span>
                      )}
                      {option.isAssigned && option.assignedTo && (
                        <span className="text-xs text-orange-600 mt-1">
                          Asignado a: {option.assignedTo}
                        </span>
                      )}
                    </div>
                    <Check
                      className={`ml-2 h-4 w-4 flex-shrink-0 ${
                        option.id === currentQuoteItemId
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                    />
                  </div>
                );

                return (
                  <CommandItem
                    key={option.id ?? "no-assign"}
                    value={option.label}
                    onSelect={() => {
                      void handleSelect(option.id);
                    }}
                    className={`p-0 ${option.isAssigned ? "opacity-60" : ""}`}
                    disabled={option.isAssigned}
                  >
                    {isTruncated ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          {option.label}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      itemContent
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
